import { AvailableType, AvailableTypes, Column, DataViewItemWithAccess, Filter, View } from '@shared/components/data-view/data-view-types';
import { isString } from 'lodash';

export const FILTER_OPERATORS = AvailableTypes;

export type FilterOperator = AvailableType;

export function isFilterOperator(value: unknown): value is FilterOperator {
  return isString(value) && (FILTER_OPERATORS as readonly string[]).includes(value);
}

export type ItemAccess = DataViewItemWithAccess;

export type FilterQueryMeta = {
  groupBy?: string;
  nested?: string;
  timezone?: string;
  should?: boolean;
};

export type FilterQueryInput = {
  field: string;
  type: FilterOperator;
  value: unknown;
  timezone?: string;
  should?: boolean;
  groupBy?: string;
  nested?: string;
  negate?: boolean;
  caseInsensitive?: boolean;
  transform?: (value: unknown) => unknown;
};

export type FilterToQuery = (context: { def: FilterDef; applied: AppliedFilter }) => FilterQueryInput[];

export type FilterDef = Filter.DataViewFilter & {
  query?: FilterQueryMeta;
  toQuery?: FilterToQuery;
};

export type AppliedFilter = {
  id: string;
  type: FilterOperator;
  value: unknown;
  timezone?: string;
};

export type AppliedFilterSchema = {
  id: string;
  value: unknown;
  type?: FilterOperator;
  timezone?: string;
};

export type ColumnDef = ItemAccess & {
  id: string;
  field: string;
  display: string;
  categoryId: string;
  type?: string;
  templateRef?: string;
  fields?: string[];
  exportAsFields?: string[];
  alwaysExports?: boolean;
};

export type AppliedColumn = ColumnDef & {
  width?: number | 'auto';
};

export type AppliedColumnSchema = Column.AppliedDataViewColumnSchema;

export type ColumnCategory = Column.DataViewColumnGalleryCategory;

export type MapConfig = View.MapConfig;

export type DataViewState = {
  filters: AppliedFilterSchema[];
  columns: AppliedColumnSchema[];
  mapConfig?: MapConfig;
};

export type ViewPreset<TState> = ItemAccess & {
  id: string;
  label: string;
  state: TState;
  isPinned?: boolean;
  isCustom?: boolean;
  basedOnViewId?: string;
};

export const DATAVIEW_CONFIG_VERSION = '4.0.0';

export type DataViewPageSchema = {
  configVersion?: string;
  domain: string;
  columns: Array<{
    id: string;
    hideFilter?: boolean;
    requiredFilter?: boolean;
    filterOnly?: boolean;
  }>;
  defaultViews: Array<ViewPreset<DataViewState>>;
};

export type StoredFilterDef = Omit<FilterDef, 'id' | 'categoryId' | 'toQuery' | 'field' | 'display'> &
  Partial<Pick<FilterDef, 'field' | 'display'>>;

export type StoredColumnDef = Omit<ColumnDef, 'id' | 'categoryId'> & {
  categoryId?: string;
  filter?: StoredFilterDef;
};

export type DataViewDomainSchema = {
  configVersion?: string;
  columns: Record<string, StoredColumnDef>;
  columnCategories: Record<string, Omit<ColumnCategory, 'id'>>;
};

export type DataViewViews = {
  views: Array<ViewPreset<DataViewState>>;
  favouriteColumnIds: string[];
};

export type DataViewUserViewsSchema = {
  configVersion?: string;
  views: Array<ViewPreset<DataViewState>>;
  pinnedViewIds: string[];
  unpinnedDefaultViewIds: string[];
  favouriteColumnIds: string[];
};
